import { Modal } from "react-bootstrap";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import UserIcon from "../../assets/img/user-icon.svg";
import IncomeSourceCard from "./IncomeSourceCard";
import useTranslator from "../../hooks/useTranslator";
import { haveValue } from "../../helper/common";
import { useEffect, useMemo, useState } from "react";

const UpdatedIncomeSourceModal = ({
  show,
  onHide,
  updateSourceList,
  handleIncomeChange,
  loadFullInfo,
}) => {
  const { _t } = useTranslator();
  const params = new URLSearchParams(window.location.search);
  const guarantor = params.get("guarantor");

  const [selectedSource, setSelectedSources] = useState({});
  const [originalSourceMap, setOriginalSourceMap] = useState({});

  useEffect(() => {
    if (!updateSourceList || !updateSourceList.length) return;

    const sourceMap = updateSourceList.reduce((acc, item) => {
      acc[item._id] = Array.isArray(item.current_selected)
        ? item.current_selected
        : [item.current_selected];

      return acc;
    }, {});

    setSelectedSources(sourceMap);
    setOriginalSourceMap(sourceMap);
  }, []);

  const toggleSelectedSource = (userId, newSource) => {
    setSelectedSources((prev) => {
      const current = prev[userId] || [];

      return {
        ...prev,
        [userId]: current.includes(newSource)
          ? current.filter((s) => s !== newSource)
          : [...current, newSource],
      };
    });
  };

  const hasChanges = useMemo(() => {
    return updateSourceList.some((user) => {
      const original = originalSourceMap[user._id] || [];
      const updated = selectedSource[user._id] || [];

      return JSON.stringify(original) !== JSON.stringify(updated);
    });
  }, [selectedSource, originalSourceMap, updateSourceList]);

  const initiateSourceChange = async () => {
    const changedUsers = updateSourceList.filter((user) => {
      const original = originalSourceMap[user._id] || [];
      const updated = selectedSource[user._id] || [];

      return JSON.stringify(original) !== JSON.stringify(updated);
    });

    if (!changedUsers.length) {
      onHide();
      return;
    }

    try {
      const requests = changedUsers.map((user) =>
        handleIncomeChange(
          selectedSource[user._id],
          haveValue(guarantor) ? true : user.type === "guarantor",
          haveValue(guarantor) ? guarantor : user._id
        )
      );

      await Promise.all(requests);

      loadFullInfo();

      setOriginalSourceMap(selectedSource);

      onHide();
    } catch (error) {
      console.error("Income source update failed:", error);
    }
  };

  return (
    <Modal
      show={show}
      className="metermodal update-income-source-modal modal-xl modalWidth_set updateSourceModal"
      centered
      onHide={onHide}
    >
      <div className="update-income-source-modal-padding">
        <h4 className="custom-modal-heading">{_t("updateIncomeSource")}</h4>
        <ClearOutlinedIcon
          className="close-button-modal"
          onClick={onHide}
          style={{ cursor: "pointer" }}
        />
      </div>
      {updateSourceList.map((userObj) => (
        <div
          key={userObj._id}
          className={
            userObj._id !== updateSourceList[updateSourceList.length - 1]._id
              ? "multi-user-wrapper update-income-source-modal-padding"
              : "update-income-source-modal-padding mb-4"
          }
        >
          {haveValue(userObj?.all_sources) && (
            <>
              <div className="earner-info update-source-info">
                <div className="earner-thumbnail-wrapper">
                  <img src={UserIcon} alt="User Icon" />
                </div>
                <h6 className="earner-name">{userObj?.all_sources?.name}</h6>
              </div>
              <h4 className="custom-modal-heading sourceHeading">
                {_t("sources")}
              </h4>
            </>
          )}
          <div className="row income-source-card-main">
            {haveValue(userObj?.all_sources) ? (
              Object.keys(userObj.all_sources).map((data) => {
                if (
                  data !== "name" &&
                  data !== "_id" &&
                  data !== "forensic_status" &&
                  data !== "step" &&
                  data !== "isForensicExists"
                ) {
                  const value = userObj.all_sources[data];

                  if (
                    (data === "itr" || data === "bank") &&
                    (value <= 0 || isNaN(value))
                  ) {
                    return null;
                  }

                  return (
                    <div className="col-md-6 income-source-card" key={data}>
                      <IncomeSourceCard
                        userId={userObj?._id}
                        amount={value}
                        type={data}
                        selectedSource={selectedSource?.[userObj?._id]}
                        changeSelectedSource={toggleSelectedSource}
                      />
                    </div>
                  );
                }
              })
            ) : (
              <div className="text-center my-2">
                {_t(
                  "incomeSourceNotFound",
                  "Text used if user doesn't have any income source"
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {updateSourceList.length > 0 && hasChanges && (
        <div
          style={{
            position: "sticky",
            width: "100%",
            bottom: "0",
            backgroundColor: "white",
          }}
        >
          <button
            className="save-changes-button"
            onClick={initiateSourceChange}
          >
            {_t("saveChanges")}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default UpdatedIncomeSourceModal;
